import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrg, { getBreadcrumbSchema, getWebPageSchema } from '@/components/feature/SchemaOrg';
import { useParams, Link } from 'react-router-dom';
import { allComicsList, allComicsByCategory, type CategoryComic } from '@/mocks/category';
import { hotRankingComics, latestComics } from '@/mocks/home';

interface ComicDetail {
  id: string;
  title: string;
  author: string;
  coverImage: string;
  category: string;
  status: 'ongoing' | 'completed';
  rating: number;
  views: string;
  latestChapter: string;
  description: string;
  tags: string[];
}

function findComicById(id: string): ComicDetail {
  const catComic = allComicsList.find((c) => c.id === id);
  if (catComic) return catComic;

  const homeComic = [...hotRankingComics, ...latestComics].find((c) => c.id === id);
  if (homeComic) {
    return {
      id: homeComic.id,
      title: homeComic.title,
      author: homeComic.author,
      coverImage: homeComic.coverImage,
      category: homeComic.category,
      status: homeComic.status,
      rating: homeComic.rating,
      views: homeComic.views,
      latestChapter: homeComic.latestChapter,
      description: homeComic.description,
      tags: homeComic.tags,
    };
  }

  return allComicsList[0];
}

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const comic = findComicById(slug || '');

  const categoryMap: Record<string, string> = {
    '恋爱甜宠': 'romance',
    '热血冒险': 'action',
    '悬疑推理': 'mystery',
    '治愈日常': 'healing',
    '奇幻异界': 'fantasy',
    '都市剧情': 'drama',
  };
  const catSlug = categoryMap[comic.category] || 'romance';

  const relatedComics: CategoryComic[] = [];
  const catComics = allComicsByCategory[catSlug] || [];
  catComics.filter((c) => c.id !== comic.id).slice(0, 3).forEach((c) => relatedComics.push(c));
  if (relatedComics.length === 0) {
    allComicsList.filter((c) => c.id !== comic.id).slice(0, 3).forEach((c) => relatedComics.push(c));
  }

  const chapterCount = Math.min(parseInt(comic.latestChapter.replace(/[^0-9]/g, '') || '8', 10), 12);
  const chapters = Array.from({ length: Math.max(chapterCount, 4) }, (_, i) => ({
    num: i + 1,
    title: `第${i + 1}话`,
    date: new Date(Date.now() - i * 7 * 24 * 60 * 60 * 1000).toLocaleDateString('zh-CN', { month: 'short', day: 'numeric' }),
    isNew: i < 3,
  }));

  return (
    <>
      <SchemaOrg type="WebPage" data={getWebPageSchema(
        `${comic.title} - ${comic.author} - 韩漫漫画网`,
        `${comic.title} - ${comic.description}。${comic.category}韩漫推荐，${comic.status === 'ongoing' ? '连载中' : '已完结'}，评分${comic.rating}，${comic.views}阅读。`,
        `http://linxituan.com/article/${comic.id}`
      )} />
      <SchemaOrg type="BreadcrumbList" data={getBreadcrumbSchema([
        { name: '首页', url: 'http://linxituan.com/' },
        { name: comic.category, url: `http://linxituan.com/category/${catSlug}` },
        { name: comic.title, url: `http://linxituan.com/article/${comic.id}` },
      ])} />
      <Navbar />
      <main className="pt-16">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-8">
          <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="面包屑导航">
            <Link to="/" className="hover:text-primary-600 cursor-pointer transition-colors">首页</Link>
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></span>
            <Link to={`/category/${catSlug}`} className="hover:text-primary-600 cursor-pointer transition-colors">{comic.category}</Link>
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line text-xs"></i></span>
            <span className="text-foreground-800 font-medium">{comic.title}</span>
          </nav>

          <article>
            <header className="flex flex-col md:flex-row gap-8 mb-12">
              <div className="md:w-1/3 flex-shrink-0">
                <div className="rounded-xl overflow-hidden shadow-sm" style={{ aspectRatio: '3/4' }}>
                  <img
                    src={comic.coverImage}
                    alt={comic.title}
                    title={`${comic.title} - 封面`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>
              <div className="md:w-2/3 flex flex-col justify-center">
                <h1 className="font-heading text-3xl md:text-4xl text-foreground-950 mb-4">{comic.title}</h1>
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                  {comic.tags.map((tag) => (
                    <span key={tag} className="text-xs bg-secondary-100 text-secondary-800 px-2.5 py-1 rounded-full">
                      {tag}
                    </span>
                  ))}
                  <span className={`text-xs px-2.5 py-1 rounded-full ${comic.status === 'ongoing' ? 'bg-primary-100 text-primary-700' : 'bg-accent-100 text-accent-700'}`}>
                    {comic.status === 'ongoing' ? '连载中' : '已完结'}
                  </span>
                </div>
                <p className="text-sm text-foreground-600 mb-2">作者：{comic.author}</p>
                <p className="text-sm text-foreground-600 mb-2">最新章节：{comic.latestChapter}</p>
                <div className="flex items-center gap-4 mb-6">
                  <span className="flex items-center gap-1 text-sm text-foreground-600">
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-star-fill text-yellow-400 text-xs"></i></span>
                    {comic.rating}
                  </span>
                  <span className="text-sm text-foreground-400">·</span>
                  <span className="text-sm text-foreground-600">{comic.views}阅读</span>
                </div>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">{comic.description}</p>
                <div className="flex items-center gap-4">
                  <Link
                    to={`/category/${catSlug}`}
                    className="px-8 py-3.5 bg-primary-500 text-white rounded-full text-sm font-semibold whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-all shadow-lg shadow-primary-500/30"
                  >
                    开始阅读
                  </Link>
                  <button className="px-8 py-3.5 text-foreground-600 hover:text-primary-600 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors border border-foreground-200 hover:border-primary-300">
                    加入书架
                  </button>
                </div>
              </div>
            </header>

            <section className="bg-secondary-50/50 rounded-2xl p-8 md:p-12 mb-12">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <p className="text-3xl font-bold text-foreground-900">{comic.views}</p>
                  <p className="text-xs text-foreground-500 mt-1">总阅读量</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-foreground-900">{(parseFloat(comic.views.replace(/[^0-9.]/g, '')) * 0.12).toFixed(0)}万</p>
                  <p className="text-xs text-foreground-500 mt-1">收藏数</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-foreground-900">{comic.rating}</p>
                  <p className="text-xs text-foreground-500 mt-1">评分</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-foreground-900">{comic.latestChapter.replace(/[^0-9]/g, '') || '0'}</p>
                  <p className="text-xs text-foreground-500 mt-1">总章节</p>
                </div>
              </div>
            </section>

            <section className="mb-16">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 text-center mb-10">章节列表</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                {chapters.map((ch) => (
                  <div
                    key={ch.num}
                    className="flex items-center justify-between p-4 bg-background-50 rounded-xl border border-background-200/70 hover:border-primary-300 hover:bg-background-100 cursor-pointer transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-secondary-100 text-sm font-medium text-secondary-700">
                        {ch.num}
                      </span>
                      <div>
                        <p className="text-sm font-medium text-foreground-800">{ch.title}</p>
                        <p className="text-xs text-foreground-400">{ch.date}</p>
                      </div>
                    </div>
                    {ch.isNew && (
                      <span className="text-xs bg-accent-100 text-accent-700 px-2 py-0.5 rounded-full font-medium">最新</span>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {relatedComics.length > 0 && (
              <section className="mb-16">
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-950 text-center mb-10">相关推荐</h2>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-5 md:gap-6">
                  {relatedComics.map((rc) => (
                    <Link
                      key={rc.id}
                      to="/category/all"
                      className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 cursor-pointer transition-all duration-300 hover:-translate-y-1"
                    >
                      <div className="relative" style={{ aspectRatio: '3/4' }}>
                        <img
                          src={rc.coverImage}
                          alt={rc.title}
                          title={`${rc.title} - 相关推荐`}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-medium text-foreground-900 group-hover:text-primary-600 transition-colors truncate">{rc.title}</h3>
                        <p className="text-xs text-foreground-500 mt-1.5">{rc.author} · {rc.latestChapter}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs flex items-center gap-0.5 text-foreground-400">
                            <span className="w-3 h-3 flex items-center justify-center"><i className="ri-star-fill text-yellow-400 text-xs"></i></span>
                            {rc.rating}
                          </span>
                          <span className="text-xs text-foreground-300">·</span>
                          <span className="text-xs text-foreground-400">{rc.category}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </article>
        </div>
      </main>
      <Footer />
    </>
  );
}